import React, { useMemo, useEffect, useState } from 'react';

interface LoaderProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  fullScreen?: boolean;
}

// Size mapping outside component to avoid recreation
const SIZE_MAP = {
  sm: 32,
  md: 64,
  lg: 96,
} as const;

// CSS keyframes - defined once globally
const LOADER_STYLES = `
  @keyframes loader-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Loader: React.FC<LoaderProps> = ({
  message = 'Loading...',
  size = 'lg',
  fullScreen = false,
}) => {
  const [isStylesReady, setIsStylesReady] = useState(false);
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined' || !document.documentElement) return false;
    return document.documentElement.classList.contains('dark');
  });
  const [showLoader, setShowLoader] = useState(false);

  // Ensure CSS is injected and ready before showing loader
  useEffect(() => {
    const ensureStylesReady = () => {
      // Check if styles already exist
      if (document.getElementById('loader-styles')) {
        setIsStylesReady(true);
        setShowLoader(true);
        return;
      }

      // Inject styles
      const style = document.createElement('style');
      style.id = 'loader-styles';
      style.textContent = LOADER_STYLES;
      document.head.appendChild(style);

      // Wait for styles to be applied
      requestAnimationFrame(() => {
        setIsStylesReady(true);
        setShowLoader(true);
      });
    };

    // Set a timeout to show loader even if styles take too long
    const timeoutId = setTimeout(() => {
      setShowLoader(true);
    }, 100); // Show after 100ms max

    ensureStylesReady();

    return () => clearTimeout(timeoutId);
  }, []);

  // Watch for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!document.documentElement) return;
      const currentIsDark = document.documentElement.classList.contains('dark');
      setIsDark(currentIsDark);
    });

    if (document.documentElement) {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => observer.disconnect();
  }, []);

  // Memoize spinner size calculation
  const spinnerSize = useMemo(() => {
    return SIZE_MAP[size] || SIZE_MAP.lg;
  }, [size]);

  // Memoize container styles
  const containerStyles = useMemo(
    () => ({
      position: fullScreen ? ('fixed' as const) : ('relative' as const),
      top: fullScreen ? 0 : 'auto',
      left: fullScreen ? 0 : 'auto',
      right: fullScreen ? 0 : 'auto',
      bottom: fullScreen ? 0 : 'auto',
      width: fullScreen ? '100%' : 'auto',
      height: fullScreen ? '100%' : 'auto',
      display: isStylesReady && showLoader ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: fullScreen
        ? isDark
          ? 'rgba(17, 24, 39, 0.95)'
          : 'rgba(255, 255, 255, 0.95)'
        : 'transparent',
      backdropFilter: fullScreen ? 'blur(8px)' : 'none',
      zIndex: fullScreen ? 9999 : 0,
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }),
    [fullScreen, isStylesReady, isDark, showLoader]
  );

  // Memoize spinner styles
  const spinnerStyles = useMemo(
    () => ({
      width: `${spinnerSize}px`,
      height: `${spinnerSize}px`,
      border: isDark ? '4px solid #374151' : '4px solid #e5e7eb',
      borderTop: '4px solid #8258c8',
      borderRight: '4px solid #6766c8',
      borderBottom: '4px solid #2c84c8',
      borderRadius: '50%',
      animation: isStylesReady ? 'loader-spin 1s linear infinite' : 'none',
      margin: '0 auto 24px',
      willChange: 'transform',
    }),
    [spinnerSize, isStylesReady, isDark]
  );

  // Memoize text styles
  const textStyles = useMemo(
    () => ({
      color: isDark ? '#f3f4f6' : '#374151',
      fontSize: '18px',
      fontWeight: '500',
      margin: 0,
      lineHeight: '1.5',
    }),
    [isDark] // Re-calculate when theme changes
  );

  return (
    <div style={containerStyles} role="status" aria-label={message}>
      <div style={{ textAlign: 'center' }}>
        <div style={spinnerStyles} aria-hidden="true" />
        <p style={textStyles}>{message}</p>
      </div>
    </div>
  );
};

export { Loader };
